import { gql } from '__generated__/gql';
import { ApolloCache } from '@apollo/client';
import { EVICT_OFFER_QUERIES, REFETCH_OFFER_QUERIES } from 'pages/offers/gql';

export const INVOICE_EVENT_DETAILS_FRAGMENT = gql(`
  fragment InvoiceEventDetailsOutput on AMEventOutput {
    id
    serviceDirectPay
    roomDirectPay
    extraDirectPay
    parkingDirectPay
    coachInvoicingRule
    noshowInvoicingRule
    attendances {
      id
      serviceDirectPay
      roomDirectPay
      extraDirectPay
      parkingDirectPay
      invoicingRule
      profile {
        id
        firstname
        lastname
      }
      days {
        id
        day
        attending
        forenoon
        morning
        lunch
        afternoon
        dinner
        room
        noShow
        morningShow
        forenoonShow
        lunchShow
        afternoonShow
        dinnerShow
        roomShow
        invoicingRule
        serviceDirectPay
        roomDirectPay
        extraDirectPay
        parkingDirectPay
      }      
    }
  }
`);

export const OFFER_INVOICE_VIEW_FRAGMENT = gql(`
  fragment WLOfferInvoiceView on WLOfferListOutput {
    commissionBase
    commissionBaseAmount
    commissionPercentage
    commissionAmountNet
    commissionAmountGross
    commissionRemark
    media {
      id
      mediaType
      name
      mimeType
      size
    }
    hotel {
      id
      businessCountry
      contracts { id type validFrom validTo }
    }
    history {
      ...WLOfferHistoryListShort
    }
    emailHistory {
      ...EmailHistoryOutputShort
    }
  }
`);

export const OFFERVERSION_INVOICE_VIEW_FRAGMENT = gql(`
  fragment WLOfferVersionInvoiceView on WLOfferVersionListOutput {
    cancellationWaiverPreview {
      applicable
      cancellationFees
      waiverBasis
      waiverBasisFirstConfirmedAt
      cancellationWaiver
      sum
      toleranceRate
      priceOnRequest
      hasFeesSnapshot
      pricesInNet
      offerId
      phases {
        daysToEvent
        rate
        phaseStart
        phaseEndExclusive
        cancelledTotal
        addedTotal
        cancellationBasis
        cancellationCosts
        priceOnRequest
        details {
          date
          serviceName
          amount
          type
          count
          paymentType
          priceOnRequest
        }
      }
      freePeriodDetails {
        date
        serviceName
        amount
        type
        count
        paymentType
        priceOnRequest
      }
    }
    invoice { costCentre accountingArea }
    sender {
      businessName
      businessAddress1
      businessAddress2
      businessAddress3
      businessAddress4
      businessZip
      businessCity
      businessCountry
      billingUid
      businessEmail
      businessPhone
      contactName
      comment
    }
    receiver {
      businessName
      businessAddress1
      businessAddress2
      businessAddress3
      businessAddress4
      businessZip
      businessCity
      businessCountry
      businessEmail
      businessPhone
      contactName
      comment
    }
  }
`);

export const INVOICE_VIEW_QUERY = gql(`
  query ViewInvoice($id: Int!) {
    viewWlOffer(id: $id) {
      ...WLOfferListView
      ...WLOfferInvoiceView
    }
    viewWlOfferInvoiceVersion(offerId: $id) {
      ...WLOfferVersionListView
      ...WLOfferVersionInvoiceView
    }
    viewWlOfferInvoiceDownloadVersion: viewWlOfferLatestVersion(offerId: $id, versionType: [RESERVATION_CONFIRMATION], versionStatus: [APPROVED, CANCELLED_CLIENT]) {
      id
      status
      type
    }
    viewWlOfferGeneratedInvoices(offerId: $id) {
      id title type status
    }
    viewWlOfferInvoiceEvent(offerId: $id) {
      ...InvoiceEventDetailsOutput
    }
    invoiceQuickPriceDehydrate(offerId: $id)
    invoiceProductTextDehydrate(offerId: $id)
  }
`);

export const LIST_INVOICE_COSTCENTRES_QUERY = gql(`
  query ListInvoiceCostCentres($offerId: Int!, $all: Boolean!) {
    listInvoiceCostCentres(offerId: $offerId, all: $all) {
      id
      name
      costCentre
      billingComment
    }
  }
`);

export const LIST_INVOICE_ACCOUNTINGAREAS_QUERY = gql(`
  query ListInvoiceAccountingAreas($offerId: Int!, $all: Boolean!) {
    listInvoiceAccountingAreas(offerId: $offerId, all: $all) {
      id
      name
    }
  }
`);

export const INVOICE_VIEW_OFFERVERSIONMEDIA_QUERY = gql(`
  query ViewOfferVersionMedia($mediaId: Int!) {
    readWlOfferVersionMediaBase64(mediaId: $mediaId)
  }
`);
export const INVOICE_VIEW_OFFERMEDIA_QUERY = gql(`
  query ViewOfferMedia($mediaId: Int!) {
    readWlOfferMediaBase64(mediaId: $mediaId)
  }
`);

export const INVOICE_APPROVE_CLASSIC_CANCELLATION_HOTEL_MUTATION = gql(`
  mutation ApproveClassicCancellationInvoiceHotel($offerVersionId: Int!, $manualCancellationWaiveNet: Float) {
    approveClassicCancellationInvoiceHotel(offerVersionId: $offerVersionId, manualCancellationWaiveNet: $manualCancellationWaiveNet) {
      ...WLOfferVersionListView
    }
  }
`);

export const INVOICE_APPROVE_PREMIUM_MUTATION = gql(`
  mutation ApprovePremiumInvoice($offerVersionId: Int!, $attendances: [WLInvoiceAttendanceInput!], $replaceLineItems: [WLOfferVersionContentBlockLineItemInput!], $extraLineItems: [WLOfferVersionContentBlockLineItemInput!], $deleteLineItemIds: [Int!], $sender: WLInvoiceSenderInput, $manualCancellationWaiveNet: Float, $commissionBase: EOfferCommissionBase, $commissionBaseAmount: Float, $commissionPercentage: Float, $commissionRemark: String, $approveAsHotel: Boolean!, $cancelAsHotel: Boolean!, $approveAsSEM: Boolean!, $rejectAsSEM: Boolean!) {
    approvePremiumInvoice(offerVersionId: $offerVersionId, attendances: $attendances, replaceLineItems: $replaceLineItems, extraLineItems: $extraLineItems, deleteLineItemIds: $deleteLineItemIds, sender: $sender, manualCancellationWaiveNet: $manualCancellationWaiveNet, commissionBase: $commissionBase, commissionBaseAmount: $commissionBaseAmount, commissionPercentage: $commissionPercentage, commissionRemark: $commissionRemark, approveAsHotel: $approveAsHotel, cancelAsHotel: $cancelAsHotel, approveAsSEM: $approveAsSEM, rejectAsSEM: $rejectAsSEM) {
      ...WLOfferVersionListView
    }
  }
`);

export const INVOICE_APPROVE_CLASSIC_HOTEL_MUTATION = gql(`
  mutation ApproveClassicInvoiceHotel($offerVersionId: Int!, $files: [WLOfferMediaInput!], $deleteMediaIds: [Int!], $sender: WLInvoiceSenderInput, $commissionBaseAmount: Float!, $commissionAmountNet: Float!) {
    approveClassicInvoiceHotel(offerVersionId: $offerVersionId, files: $files, deleteMediaIds: $deleteMediaIds, sender: $sender, commissionBaseAmount: $commissionBaseAmount, commissionAmountNet: $commissionAmountNet) {
      ...WLOfferVersionListView
    }
  }
`);

export const INVOICE_APPROVE_CLASSIC_SEM_MUTATION = gql(`
  mutation ApproveClassicInvoiceSEM($offerVersionId: Int!, $sender: WLInvoiceSenderInput, $commissionBase: EOfferCommissionBase!, $commissionBaseAmount: Float!, $commissionPercentage: Float!, $commissionRemark: String) {
    approveClassicInvoiceSEM(offerVersionId: $offerVersionId, sender: $sender, commissionBase: $commissionBase, commissionBaseAmount: $commissionBaseAmount, commissionPercentage: $commissionPercentage, commissionRemark: $commissionRemark) {
      ...WLOfferVersionListView
    }
  }
`);

export const INVOICE_GENERATE_MUTATION = gql(`
  mutation GenerateClientInvoices($offerVersionId: Int!, $costCentres: [String!]!) {
    generateClientInvoices(offerVersionId: $offerVersionId, costCentres: $costCentres) {
      id
      title
      type
      status
    }
  }
`);

export const EVICT_INVOICE_QUERIES = (cache: ApolloCache<any>) => {
  EVICT_OFFER_QUERIES(cache);
};

export const REFETCH_INVOICE_QUERIES = (offerId?: number | undefined, offerVersionId?: number | undefined) => [
  ...(offerId
    ? [
      {
        query: INVOICE_VIEW_QUERY,
        variables: { id: offerId },
      },
      {
        query: LIST_INVOICE_COSTCENTRES_QUERY,
        variables: { offerId, all: true },
      },
      {
        query: LIST_INVOICE_COSTCENTRES_QUERY,
        variables: { offerId, all: false },
      },
      {
        query: LIST_INVOICE_ACCOUNTINGAREAS_QUERY,
        variables: { offerId, all: true },
      },
      {
        query: LIST_INVOICE_ACCOUNTINGAREAS_QUERY,
        variables: { offerId, all: false },
      },
    ]
    : []),
  ...REFETCH_OFFER_QUERIES(offerId, offerVersionId),
];
